// src/components/CookieBanner/config.ts
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

/**
 * CookieConsent library:
 *   - API reference: https://cookieconsent.orestbida.com/reference/api-reference.html
 *
 * Category rationale for this application:
 *
 * - Noodzakelijk (necessary)
 *   • Strikt vereist voor beveiliging, inloggen, feature flags en betalingen.
 *   • Voorbeeld: Stripe cookie __stripe_mid op .app.bndl.nl.
 *   • Inclusief vergelijkbare technologieën zoals localStorage voor AWS Cognito
 *     (CognitoIdentityServiceProvider-*) en Stripe (bv. opslag op m.stripe.network).
 *
 * - Analyse (analytics)
 *   • Meten hoe de applicatie wordt gebruikt (Google Analytics 4).
 *   • Voorbeelden: _ga en _ga_* cookies op .bndl.nl.
 */

type Args = {
  COOKIE_CONSENT_STORAGE_KEY: string;
  cookiePrivacyUrl?: string;
  onFirstConsent: (param: { cookie: CookieConsent.CookieValue }) => void;
  onChange: (param: {
    cookie: CookieConsent.CookieValue;
    changedCategories: Array<string>;
    changedServices: { [key: string]: Array<string> };
  }) => void;
  onConsent: (param: { cookie: CookieConsent.CookieValue }) => void;
};

export const pluginConfig = ({
  COOKIE_CONSENT_STORAGE_KEY,
  cookiePrivacyUrl,
  onFirstConsent,
  onChange,
  onConsent,
}: Args): CookieConsentConfig => {
  const row = (
    name: string,
    vendor: string,
    domain: string,
    description: string,
    expiration: string,
  ) => ({ name, vendor, domain, description, expiration });

  return {
    onConsent,
    onFirstConsent,
    onChange,
    disablePageInteraction: true,
    cookie: {
      name: COOKIE_CONSENT_STORAGE_KEY,
    },

    guiOptions: {
      consentModal: {
        layout: 'cloud',
        position: 'bottom',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        position: 'left',
        equalWeightButtons: false,
        flipButtons: false,
      },
    },

    // Only two categories: necessary (always on) and analytics (opt-in)
    categories: {
      necessary: { readOnly: true, enabled: true },
      analytics: {},
    },

    language: {
      default: 'nl',
      translations: {
        nl: {
          consentModal: {
            title: 'Deze applicatie maakt gebruik van cookies',
            description:
              'We gebruiken cookies om de applicatie veilig te laten werken en om te begrijpen hoe deze wordt gebruikt. U kunt zelf aangeven of we daarnaast analysecookies mogen plaatsen.',
            acceptAllBtn: 'Accepteer alle',
            showPreferencesBtn: 'Beheer voorkeuren',
            footer: cookiePrivacyUrl
              ? `<a href='${cookiePrivacyUrl}' target="_blank" rel="noreferrer noopener">Lees meer over cookies</a>`
              : undefined,
          },
          preferencesModal: {
            title: 'Cookievoorkeuren',
            acceptAllBtn: 'Accepteer alle',
            acceptNecessaryBtn: 'Alleen noodzakelijk',
            savePreferencesBtn: 'Voorkeuren opslaan',
            closeIconLabel: 'Sluiten',
            sections: [
              // Intro
              {
                title: 'Deze applicatie maakt gebruik van cookies',
                description:
                  'We gebruiken cookies en vergelijkbare technologieën (zoals localStorage) om de applicatie veilig te laten werken (inloggen, betalingen, feature flags) en om anonieme statistieken te verzamelen. Niet-essentiële cookies gebruiken we alleen met uw toestemming.',
              },

              /**
               * NECESSARY — auth / feature flags / payments (Stripe, Cognito)
               *
               * Cookies in deze categorie (op basis van wat je hebt laten zien):
               *   - __stripe_mid op .app.bndl.nl
               *
               * Daarnaast wordt localStorage gebruikt voor:
               *   - AWS Cognito sessies (CognitoIdentityServiceProvider-*)
               *   - Stripe risicobeoordeling (opslag op m.stripe.network)
               */
              {
                title:
                  'Noodzakelijk <span class="pm__badge">Altijd ingeschakeld</span>',
                linkedCategory: 'necessary',
                description:
                  'Noodzakelijke cookies en vergelijkbare technologieën maken basisfuncties, beveiliging, inloggen en betalingen mogelijk. Zonder deze kan de applicatie niet naar behoren werken. Dit omvat ook technische opslag in localStorage, zoals AWS Cognito-gegevens voor authenticatie en Stripe-opslag voor risicobeoordeling.',
                cookieTable: {
                  headers: {
                    name: 'Naam',
                    domain: 'Domein',
                    vendor: 'Leverancier',
                    description: 'Beschrijving',
                    expiration: 'Vervaldatum',
                  },
                  body: [
                    row(
                      '__stripe_mid',
                      'Stripe',
                      '.app.bndl.nl',
                      'Gebruikt door Stripe voor fraudepreventie en om een apparaat in de context van betalingen te herkennen.',
                      '± 1 jaar (door Stripe beheerd)',
                    ),
                    row(
                      '__stripe_sid',
                      'Stripe',
                      '.app.bndl.nl',
                      'Sessiecookie van Stripe dat wordt gebruikt voor betalingen en fraudepreventie.',
                      'Sessie / korte duur (door Stripe beheerd)',
                    ),
                    row(
                      '_crocs_cookie_consent',
                      'BNDL',
                      '.bndl.nl',
                      'Onthoudt welke cookievoorkeuren u heeft ingesteld in deze applicatie.',
                      'Max. 12 maanden',
                    ),
                  ],
                },
              },

              /**
               * ANALYTICS — GA4
               *
               * Exact de GA4 cookies uit je screenshot.
               */
              {
                title: 'Analyse',
                linkedCategory: 'analytics',
                description:
                  'Analysecookies helpen ons te begrijpen hoe de applicatie wordt gebruikt, zodat we deze kunnen verbeteren. We verzamelen en rapporteren deze gegevens op geaggregeerde, niet-persoonsgerichte basis.',
                cookieTable: {
                  headers: {
                    name: 'Naam',
                    domain: 'Domein',
                    vendor: 'Leverancier',
                    description: 'Beschrijving',
                    expiration: 'Vervaldatum',
                  },
                  body: [
                    row(
                      '_ga',
                      'Google Analytics 4',
                      '.bndl.nl',
                      'Hoofd-cookie van GA4 om unieke gebruikers te onderscheiden (op browserniveau).',
                      '± 2 jaar',
                    ),
                    row(
                      '_ga_*',
                      'Google Analytics 4',
                      '.bndl.nl',
                      'Aanvullende GA4 cookies met measurement-ID (bijv. _ga_GPQYJH2XSJ) voor sessiestatus en dataverzameling voor deze applicatie.',
                      '± 2 jaar',
                    ),
                    row(
                      '_clck',
                      'Microsoft Clarity',
                      '.bndl.nl',
                      'Identificeert terugkerende gebruikers om gebruiksgedrag te analyseren.',
                      'tot 1 jaar (door Microsoft beheerd)',
                    ),
                    row(
                      '_clsk',
                      'Microsoft Clarity',
                      '.bndl.nl',
                      'Voegt meerdere paginabezoeken samen in één sessie voor analyse.',
                      '1 dag (door Microsoft beheerd)',
                    ),
                    row(
                      'CLID',
                      'Microsoft Clarity',
                      '.clarity.ms',
                      'Registreert wanneer een gebruiker voor het eerst Clarity bezoekt vanaf een bepaalde browser.',
                      'tot 1 jaar (door Microsoft beheerd)',
                    ),
                    row(
                      'MUID',
                      'Microsoft Clarity / Microsoft',
                      '.clarity.ms',
                      'Uniek ID dat wordt gebruikt om gebruikers over Microsoft-domeinen heen te herkennen voor analyse.',
                      'tot 1 jaar (door Microsoft beheerd)',
                    ),
                  ],
                },
              },

              // Legal/help
              {
                title: 'Meer informatie',
                description: `
                  Niet-essentiële cookies gebruiken wij alleen met uw toestemming (AVG art. 6(1)(a)).
                  <br /><br />
                  U kunt uw toestemming altijd wijzigen via de ronde cookieknop linksonder in de applicatie.
                  <br /><br />
                  Vragen? <a href="mailto:contact@bndl.nl">contact@bndl.nl</a>. Vermeld a.u.b. uw toestemmings-ID en datum.
                `,
              },
            ],
          },
        },
      },
    },
  };
};
